<template>
  <button
    class="championship-match-card"
    :class="cardModifierClass"
    :disabled="isPlaceholder"
    @click="handleClick"
    type="button"
  >
    <div class="match-status-badge" :class="badgeClass">{{ statusLabel }}</div>
    <div class="match-title">{{ matchTitle }}</div>

    <div class="match-rows">
      <div class="match-row" :class="rowClass('A')">
        <span v-if="rowStatusLabel('A')" class="row-status">{{ rowStatusLabel('A') }}</span>
        <div class="row-name-block">
          <span class="row-avatar">
            <img v-if="participantAvatar('A')" :src="participantAvatar('A')" :alt="participantName('A')" />
            <i v-else class="fa-regular fa-user" aria-hidden="true"></i>
          </span>
          <span class="row-name special-text">{{ participantName('A') }}</span>
        </div>
      </div>
      <div class="match-vs">{{ $t('championship.vs') }}</div>
      <div class="match-row" :class="rowClass('B')">
        <span v-if="rowStatusLabel('B')" class="row-status">{{ rowStatusLabel('B') }}</span>
        <div class="row-name-block">
          <span class="row-avatar">
            <img v-if="participantAvatar('B')" :src="participantAvatar('B')" :alt="participantName('B')" />
            <i v-else class="fa-regular fa-user" aria-hidden="true"></i>
          </span>
          <span class="row-name special-text">{{ participantName('B') }}</span>
        </div>
      </div>
    </div>
  </button>
</template>

<script>
export default {
  name: 'ChampionshipMatchCard',
  props: {
    match: { type: Object, required: true },
    participantsByUserId: { type: Object, default: () => ({}) },
    stage: { type: Object, default: null },
  },
  computed: {
    isPlaceholder() { return this.match?.placeholder === true; },
    isCompleted() { return !this.isPlaceholder && (this.match.status === 'COMPLETED' || this.match.status === 'WALKOVER'); },
    isInProgress() { return !this.isPlaceholder && (this.match.status === 'IN_PROGRESS' || this.match.status === 'READY'); },
    isUpcoming() { return this.isPlaceholder || (!this.isCompleted && !this.isInProgress); },
    statusLabel() {
      if (this.isCompleted) return this.$t('championship.matchStatus.ended');
      if (this.isInProgress) return this.$t('championship.matchStatus.current');
      return this.$t('championship.matchStatus.upcoming');
    },
    badgeClass() {
      if (this.isCompleted) return 'badge-ended';
      if (this.isInProgress) return 'badge-current';
      return 'badge-upcoming';
    },
    cardModifierClass() {
      if (this.isPlaceholder) return 'card-placeholder';
      if (this.isCompleted) return 'card-ended';
      if (this.isInProgress) return 'card-current';
      return 'card-upcoming';
    },
    matchTitle() {
      // Per Figma: short title is "Competition Name" — falls back to a stage
      // + match-order combo when we don't have a tournament name to surface.
      const stageLabel = this.stage?.stageType ? this.$t(`championship.stage.${this.stage.stageType}`) : '';
      const order = this.match.matchOrder || '?';
      return stageLabel
        ? `${stageLabel} · ${this.$t('championship.matchN', { n: order })}`
        : this.$t('championship.matchN', { n: order });
    },
  },
  methods: {
    participant(slot) {
      const userId = slot === 'A' ? this.match.userAId : this.match.userBId;
      if (!userId) return null;
      return this.participantsByUserId[userId] || null;
    },
    // Skinned championships replace the username with the skin character name
    // and the avatar with the skin image (the championship participants payload
    // carries skinCharacterName / skinImageUrl when the theme is on).
    participantName(slot) {
      const userId = slot === 'A' ? this.match.userAId : this.match.userBId;
      if (!userId) return this.$t('championship.toBeDefined');
      const p = this.participantsByUserId[userId];
      return p?.skinCharacterName || p?.login || this.$t('championship.userFallback', { id: userId });
    },
    participantAvatar(slot) {
      return this.participant(slot)?.skinImageUrl || null;
    },
    isWinner(slot) {
      if (!this.isCompleted) return false;
      const winnerId = this.match.winnerUserId;
      const userId = slot === 'A' ? this.match.userAId : this.match.userBId;
      return winnerId && userId && winnerId === userId;
    },
    isLoser(slot) {
      if (!this.isCompleted) return false;
      const winnerId = this.match.winnerUserId;
      const userId = slot === 'A' ? this.match.userAId : this.match.userBId;
      return winnerId && userId && winnerId !== userId;
    },
    isPlayingSlot(slot) {
      if (!this.isInProgress) return false;
      const userId = slot === 'A' ? this.match.userAId : this.match.userBId;
      return !!userId && !!this.match.userAId && !!this.match.userBId;
    },
    rowClass(slot) {
      const userId = slot === 'A' ? this.match.userAId : this.match.userBId;
      if (!userId) return 'row-tbd';
      if (this.isWinner(slot)) return 'row-winner';
      if (this.isLoser(slot)) return 'row-loser';
      if (this.isPlayingSlot(slot)) return 'row-playing';
      return 'row-default';
    },
    rowStatusLabel(slot) {
      if (this.isWinner(slot)) return this.$t('championship.rowStatus.winner');
      if (this.isPlayingSlot(slot)) return this.$t('championship.rowStatus.playing');
      return null;
    },
    handleClick() {
      if (this.isPlaceholder) return;
      this.$emit('match-click', this.match);
    },
  },
};
</script>

<style lang="scss" scoped>
.championship-match-card {
  background: #ffffff;
  border: 1px solid #ededed;
  border-radius: 10px;
  padding: 14px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  cursor: pointer;
  font-family: 'Avenir', sans-serif;
  width: 100%;
  position: relative;
  transition: box-shadow 0.18s ease, border-color 0.18s ease, transform 0.18s ease;

  &:hover:not(:disabled) {
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: default;
  }

  &.card-placeholder {
    opacity: 0.55;
  }
}

.match-status-badge {
  // Sit at the top-left with breathing room from the card border (matches Figma).
  align-self: flex-start;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 10px;
  letter-spacing: 0.4px;
  text-transform: capitalize;

  &.badge-ended {
    background: #d6f5e3;
    color: #009f63;
  }
  &.badge-current {
    background: #ffe9d6;
    color: #c25c08;
  }
  &.badge-upcoming {
    background: #fff5d1;
    color: #8a6d00;
  }
}

.match-title {
  font-size: 12px;
  font-weight: 600;
  color: #1b1b1b;
  line-height: 1.2;
}

.match-rows {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.match-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #f8f8f8;
  border-radius: 6px;
  padding: 6px 8px 6px 14px;
  position: relative;

  &.row-default {
    background: #f8f8f8;
  }

  &.row-winner {
    background: #d6f5e3;
  }

  &.row-playing {
    // Same green wash as the winner row but lighter (Figma's "winning" state
    // uses the same fill family with a brighter tint).
    background: #e6f9ee;
  }

  &.row-loser {
    background: #f8f8f8;
    opacity: 0.55;
  }

  &.row-tbd {
    background: transparent;
    border: 1px dashed #e0e0e0;
    color: #9a9a9a;
    font-style: italic;
    padding-left: 8px;
  }

  // Left accent stripe for active states — sits inside the row's rounded edge
  // so it visually anchors the participant block without shifting layout.
  &.row-winner::before,
  &.row-playing::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 8px;
    bottom: 8px;
    width: 3px;
    background: #00d97e;
    border-radius: 2px;
  }
}

.row-status {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #009f63;

  .row-winner & { color: #009f63; }
  .row-playing & { color: #009f63; }
  .row-loser & { color: #9a9a9a; }
}

.row-name-block {
  display: flex;
  align-items: center;
  gap: 8px;
}

.row-avatar {
  width: 18px;
  height: 18px;
  background: #e6e6e6;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  i {
    font-size: 9px;
    color: #9a9a9a;
  }

  .row-tbd & {
    background: transparent;
    border: 1px dashed #cccccc;
    i { color: #cccccc; }
  }
}

.row-name {
  font-size: 13px;
  font-weight: 500;
  color: #1b1b1b !important;

  .row-tbd & { color: #9a9a9a !important; }
  .row-loser & { color: #6a6a6a !important; }
}

.match-vs {
  font-size: 10px;
  text-align: center;
  color: #9a9a9a;
  font-weight: 600;
}

.theme--dark .championship-match-card {
  background: #1f1f1f;
  border-color: rgba(255, 255, 255, 0.08);

  .match-title { color: #f8f8f8; }
  .row-default { background: rgba(255, 255, 255, 0.04); }
  .row-tbd { border-color: rgba(255, 255, 255, 0.15); }
  .row-name { color: #f8f8f8 !important; }
  .row-loser .row-name { color: rgba(255, 255, 255, 0.5) !important; }
  .row-tbd .row-name { color: rgba(255, 255, 255, 0.45) !important; }
  .row-avatar { background: rgba(255, 255, 255, 0.08); i { color: rgba(255, 255, 255, 0.55); } }
  .row-tbd .row-avatar { background: transparent; }
}
</style>
